import type {
  CalibreCacheSnapshot,
  CalibreOverrides,
} from '@watchkeeper/shared-types';
import type {
  CalibreData,
  CalibreTier,
  CalibreTierOverrides,
} from '../../services/firebase';
import { OVERRIDABLE_FIELDS } from './calibreTiers';

/**
 * Phase 3a helpers for the ref editor's per-ref calibre link.
 *
 * A `WatchReference.calibreCache` is the linked calibre's EFFECTIVE spec (base +
 * tier overrides already applied by the cache trigger). Per-ref
 * `calibreOverrides` layer on top of it. To render/edit those overrides we reuse
 * the SAME tier-override machinery (OVERRIDABLE_FIELDS, applyTierOverrides,
 * effectiveAccuracy, the override controls) by projecting the cache snapshot
 * onto a `CalibreData` shape and treating the per-ref overrides exactly like a
 * tier's overrides — the two override surfaces target the identical field set
 * (`CalibreOverrides` is an alias of `CalibreTierOverrides`).
 */

/**
 * Project a `CalibreCacheSnapshot` onto the `CalibreData` shape the tier-override
 * helpers expect. The override key set is a strict subset of the snapshot's
 * fields (both mirror `CalibreData`), so every override key copies across 1:1;
 * the extra display fields are copied for completeness. `CalibreData` has every
 * field optional, so a partial projection is a valid value.
 */
export function cacheToBaseData(
  cache: CalibreCacheSnapshot | undefined,
): CalibreData {
  if (!cache) return {};
  return {
    movementType: cache.movementType,
    baseMovement: cache.baseMovement,
    jewels: cache.jewels,
    powerReserveHours: cache.powerReserveHours,
    frequencyVph: cache.frequencyVph,
    positionsAdjusted: cache.positionsAdjusted,
    accuracyLowerSecPerDay: cache.accuracyLowerSecPerDay,
    accuracyUpperSecPerDay: cache.accuracyUpperSecPerDay,
    accuracySymmetric: cache.accuracySymmetric,
    diameterMm: cache.diameter,
    heightMm: cache.height,
    hacking: cache.hacking,
    manualWinding: cache.manualWinding,
    quickSetDate: cache.quickSetDate,
    windingDirection: cache.windingDirection,
    isChronometer: cache.isChronometer,
    isCosc: cache.isCosc,
    isGlashuetteRegulation: cache.isGlashuetteRegulation,
    isGenevaSeal: cache.isGenevaSeal,
    isGrandSeikoStandard: cache.isGrandSeikoStandard,
    isMETAS: cache.isMETAS,
    isGMT: cache.isGMT,
    isChronograph: cache.isChronograph,
    isInHouse: cache.isInHouse,
    antiShockSystem: cache.antiShockSystem,
    rotorType: cache.rotorType,
    columnWheel: cache.columnWheel,
    regulatorSystem: cache.regulatorSystem,
    hairspring: cache.hairspring,
    doubleBarrel: cache.doubleBarrel,
    escapement: cache.escapement,
    batteryType: cache.batteryType,
    batteryLifeYears: cache.batteryLifeYears,
    shockResistanceG: cache.shockResistanceG,
    antiMagneticAm: cache.antiMagneticAm,
    antiMagneticGauss: cache.antiMagneticGauss,
    chronograph: cache.chronograph,
    date: cache.date,
    hands: cache.hands,
    additionals: cache.additionals,
    astronomical: cache.astronomical,
    acoustic: cache.acoustic,
  };
}

/** Number of per-ref override fields actually set (non-undefined). */
export function countCalibreOverrides(
  overrides: CalibreTierOverrides | CalibreOverrides | undefined,
): number {
  if (!overrides) return 0;
  return OVERRIDABLE_FIELDS.reduce(
    (n, f) =>
      (overrides as CalibreTierOverrides)[f.key] !== undefined ? n + 1 : n,
    0,
  );
}

export type CacheFieldKind = 'text' | 'number' | 'boolean' | 'array';

export interface CacheDisplayField {
  key: keyof CalibreCacheSnapshot;
  label: string;
  kind: CacheFieldKind;
  /**
   * For lookup-backed fields, the lookup collection whose `displayName` should
   * be shown instead of the raw stored id (e.g. `mecha-quartz` → `Mecha-quartz`).
   * The panel resolves via the caller-supplied resolver, falling back to raw.
   */
  lookup?: string;
  /**
   * True for `manufacturerId`: resolve the id to a `movement_manufacturers`
   * doc name via the caller-supplied map instead of a lookup collection.
   */
  manufacturer?: boolean;
}

/**
 * Build a display-only `CalibreCacheSnapshot` from a resolved calibre entry +
 * the selected tier, WITHOUT waiting for the ref-write cache trigger. Lets the
 * ref editor render the effective spec panel the instant a calibre is linked
 * (before `calibreCache` is populated). Mirrors {@link cacheToBaseData} inverted
 * — the only renamed fields are `diameter`/`diameterMm` and `height`/`heightMm`
 * — and overlays the tier's overrides on the overridable keys exactly as the
 * server trigger does. Returns `undefined` when the entry has no `data`.
 *
 * NOTE: kept field-aligned with the CF cache trigger; values are display-only,
 * so a small transient divergence before the real cache lands is harmless.
 */
export function calibreDataToCacheSnapshot(
  entry:
    | {
        name?: string;
        manufacturerId?: string | null;
        brandId?: string;
        data?: CalibreData;
      }
    | undefined
    | null,
  tier: CalibreTier | undefined,
): CalibreCacheSnapshot | undefined {
  const d = entry?.data;
  if (!d) return undefined;
  const ov: CalibreTierOverrides = tier?.overrides ?? {};
  // Tier override wins when set (non-undefined), else the base calibre value.
  const eff = <T>(override: T | undefined, base: T | undefined): T | undefined =>
    override !== undefined ? override : base;
  return {
    canonicalName: entry?.name,
    manufacturerId: entry?.manufacturerId ?? undefined,
    brandId: entry?.brandId,
    movementType: d.movementType,
    baseMovement: d.baseMovement,
    frequencyVph: d.frequencyVph,
    diameter: d.diameterMm,
    height: d.heightMm,
    batteryType: d.batteryType,
    batteryLifeYears: d.batteryLifeYears,
    // Durability trio moved into the overridable block below (Phase 3 tier
    // overrides, v1.94.0). Ball's Amortiser® shock upgrade and soft-iron
    // anti-magnetic tiers raise these ratings above the base movement's.
    windingDirection: d.windingDirection,
    manualWinding: d.manualWinding,
    isChronograph: d.isChronograph,
    isGMT: d.isGMT,
    isInHouse: d.isInHouse,
    columnWheel: d.columnWheel,
    doubleBarrel: d.doubleBarrel,
    date: d.date,
    hands: d.hands,
    chronograph: d.chronograph,
    astronomical: d.astronomical,
    acoustic: d.acoustic,
    additionals: d.additionals,
    // Overridable keys — tier override overlays the base value.
    jewels: eff(ov.jewels, d.jewels),
    powerReserveHours: eff(ov.powerReserveHours, d.powerReserveHours),
    positionsAdjusted: eff(ov.positionsAdjusted, d.positionsAdjusted),
    accuracyLowerSecPerDay: eff(
      ov.accuracyLowerSecPerDay,
      d.accuracyLowerSecPerDay,
    ),
    accuracyUpperSecPerDay: eff(
      ov.accuracyUpperSecPerDay,
      d.accuracyUpperSecPerDay,
    ),
    accuracySymmetric: eff(ov.accuracySymmetric, d.accuracySymmetric),
    isChronometer: eff(ov.isChronometer, d.isChronometer),
    isCosc: eff(ov.isCosc, d.isCosc),
    isGlashuetteRegulation: eff(
      ov.isGlashuetteRegulation,
      d.isGlashuetteRegulation,
    ),
    isGenevaSeal: eff(ov.isGenevaSeal, d.isGenevaSeal),
    isGrandSeikoStandard: eff(ov.isGrandSeikoStandard, d.isGrandSeikoStandard),
    isMETAS: eff(ov.isMETAS, d.isMETAS),
    hacking: eff(ov.hacking, d.hacking),
    quickSetDate: eff(ov.quickSetDate, d.quickSetDate),
    shockResistanceG: eff(ov.shockResistanceG, d.shockResistanceG),
    antiMagneticAm: eff(ov.antiMagneticAm, d.antiMagneticAm),
    antiMagneticGauss: eff(ov.antiMagneticGauss, d.antiMagneticGauss),
    regulatorSystem: eff(ov.regulatorSystem, d.regulatorSystem),
    hairspring: eff(ov.hairspring, d.hairspring),
    escapement: eff(ov.escapement, d.escapement),
    antiShockSystem: eff(ov.antiShockSystem, d.antiShockSystem),
    rotorType: eff(ov.rotorType, d.rotorType),
    tierId: tier?.id,
    tierName: tier?.name,
  };
}

/**
 * Read-only display config for the "Effective Calibre Spec (from cache)" panel.
 * Groups the snapshot into logical sections; the accuracy trio is rendered
 * separately (via formatAccuracy), so its three keys are absent here. Only
 * fields the snapshot actually carries are rendered (see the panel component).
 */
export const CACHE_DISPLAY_SECTIONS: {
  title: string;
  fields: CacheDisplayField[];
}[] = [
  {
    title: 'Identity',
    fields: [
      { key: 'canonicalName', label: 'Canonical name', kind: 'text' },
      { key: 'manufacturerId', label: 'Manufacturer', kind: 'text', manufacturer: true },
      { key: 'brandId', label: 'Brand ID', kind: 'text' },
      { key: 'movementType', label: 'Movement type', kind: 'text', lookup: 'lookup_movement_types' },
      { key: 'baseMovement', label: 'Base movement', kind: 'text' },
    ],
  },
  {
    title: 'Specs',
    fields: [
      { key: 'jewels', label: 'Jewels', kind: 'number' },
      { key: 'powerReserveHours', label: 'Power reserve (h)', kind: 'number' },
      { key: 'frequencyVph', label: 'Frequency (VPH)', kind: 'number' },
      { key: 'diameter', label: 'Diameter (mm)', kind: 'number' },
      { key: 'height', label: 'Height (mm)', kind: 'number' },
      { key: 'positionsAdjusted', label: 'Positions adjusted', kind: 'number' },
      { key: 'batteryLifeYears', label: 'Battery life (y)', kind: 'number' },
      { key: 'batteryType', label: 'Battery type', kind: 'text' },
      { key: 'shockResistanceG', label: 'Shock resistance (G)', kind: 'number' },
      { key: 'antiMagneticAm', label: 'Anti-magnetic (A/m)', kind: 'number' },
      { key: 'antiMagneticGauss', label: 'Anti-magnetic (Gauss)', kind: 'number' },
      { key: 'windingDirection', label: 'Winding direction', kind: 'text', lookup: 'lookup_winding_directions' },
      { key: 'antiShockSystem', label: 'Anti-shock system', kind: 'text', lookup: 'lookup_shock_protection' },
      { key: 'escapement', label: 'Escapement', kind: 'text', lookup: 'lookup_escapement_type' },
      { key: 'regulatorSystem', label: 'Regulator system', kind: 'text', lookup: 'lookup_regulation' },
      { key: 'hairspring', label: 'Hairspring', kind: 'text', lookup: 'lookup_balance_spring' },
      { key: 'rotorType', label: 'Rotor type', kind: 'text' },
    ],
  },
  {
    title: 'Certifications & features',
    fields: [
      { key: 'isChronometer', label: 'Chronometer', kind: 'boolean' },
      { key: 'isCosc', label: 'COSC', kind: 'boolean' },
      { key: 'isGlashuetteRegulation', label: 'Glashütte Regulation', kind: 'boolean' },
      { key: 'isGenevaSeal', label: 'Geneva Seal', kind: 'boolean' },
      { key: 'isGrandSeikoStandard', label: 'Grand Seiko Standard', kind: 'boolean' },
      { key: 'isMETAS', label: 'METAS', kind: 'boolean' },
      { key: 'isInHouse', label: 'In-house', kind: 'boolean' },
      { key: 'hacking', label: 'Hacking', kind: 'boolean' },
      { key: 'manualWinding', label: 'Manual winding', kind: 'boolean' },
      { key: 'quickSetDate', label: 'Quick-set date', kind: 'boolean' },
      { key: 'isChronograph', label: 'Chronograph', kind: 'boolean' },
      { key: 'isGMT', label: 'GMT', kind: 'boolean' },
      { key: 'columnWheel', label: 'Column wheel', kind: 'boolean' },
      { key: 'doubleBarrel', label: 'Double barrel', kind: 'boolean' },
    ],
  },
  {
    title: 'Complications',
    fields: [
      { key: 'date', label: 'Date', kind: 'array' },
      { key: 'hands', label: 'Hands', kind: 'array' },
      { key: 'chronograph', label: 'Chronograph', kind: 'array' },
      { key: 'astronomical', label: 'Astronomical', kind: 'array' },
      { key: 'acoustic', label: 'Acoustic', kind: 'array' },
      { key: 'additionals', label: 'Additional', kind: 'array' },
    ],
  },
];
